import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosPost from '../helpers/axios-helper';
import '../styles/Blog.css';

export default function TravelGuide() {
    const { countryId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [travelGuide, setTravelGuide] = useState(null);
    const [countryName, setCountryName] = useState('');
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxImages, setLightboxImages] = useState([]);
    const [lightboxIndex, setLightboxIndex] = useState(0);

    const closeLightbox = () => setLightboxOpen(false);
    const showPrev = () => setLightboxIndex((i) => (i - 1 + lightboxImages.length) % lightboxImages.length);
    const showNext = () => setLightboxIndex((i) => (i + 1) % lightboxImages.length);

    // Keyboard navigation for lightbox
    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') closeLightbox();
            if (e.key === 'ArrowLeft') showPrev();
            if (e.key === 'ArrowRight') showNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightboxOpen, lightboxImages.length]);

    const formatDate = (unix) => {
        if (!unix) return '';
        try { return new Date(unix * 1000).toLocaleDateString(); } catch { return ''; }
    };

    // Render blocks exactly like Blog.js
    const renderBlocks = (blocks) => {
        if (!Array.isArray(blocks)) return null;
        return blocks.map((b, idx) => {
            if (b.type === 'header') {
                return (
                    <h2 key={idx} className="blog-block-header">
                        {b.content}
                    </h2>
                );
            }
            if (b.type === 'image') {
                const c = b.content;
                if (typeof c === 'string') {
                    return (
                        <div key={idx} className="blog-image-item">
                            <img
                                src={c}
                                alt=""
                                className="blog-block-image blog-image-clickable"
                                onClick={() => {
                                    setLightboxImages([c]);
                                    setLightboxIndex(0);
                                    setLightboxOpen(true);
                                }}
                            />
                        </div>
                    );
                }
                if (c && Array.isArray(c.images)) {
                    const images = c.images;
                    return (
                        <div key={idx} className={images.length > 1 ? 'blog-images-grid' : ''}>
                            {images.map((im, i) => (
                                <div key={`img-${idx}-${i}`} className="blog-image-item">
                                    <img
                                        src={im.url}
                                        alt={im.caption || ''}
                                        className="blog-block-image blog-image-clickable"
                                        onClick={() => {
                                            const urls = images.map(m => m.url);
                                            setLightboxImages(urls);
                                            setLightboxIndex(i);
                                            setLightboxOpen(true);
                                        }}
                                    />
                                    {im.caption && (
                                        <div className="blog-image-caption">{im.caption}</div>
                                    )}
                                </div>
                            ))}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'video') {
                const c = b.content;
                if (c && c.url) {
                    const formatVideoUrl = (url) => {
                        if (url.includes('youtube.com/watch')) {
                            const videoId = new URL(url).searchParams.get('v');
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('youtu.be')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://www.youtube.com/embed/${videoId}`;
                        }
                        if (url.includes('vimeo.com')) {
                            const videoId = url.split('/').pop().split('?')[0];
                            if (videoId) return `https://player.vimeo.com/video/${videoId}`;
                        }
                        return url;
                    };
                    return (
                        <div key={idx} className="blog-video-container">
                            <iframe
                                src={formatVideoUrl(c.url)}
                                title={c.caption || 'Video'}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                            {c.caption && (
                                <div className="blog-image-caption">{c.caption}</div>
                            )}
                        </div>
                    );
                }
                return null;
            }
            if (b.type === 'text') {
                const c = b.content;
                try {
                    if (typeof c === 'string') {
                        if (c.startsWith('<') || c.includes('<p>') || c.includes('<h')) {
                            return (
                                <div 
                                    key={idx} 
                                    className="blog-block-html"
                                    dangerouslySetInnerHTML={{ __html: c }}
                                />
                            );
                        }
                        if (c.startsWith('{')) {
                            const raw = JSON.parse(c);
                            if (raw && Array.isArray(raw.blocks)) {
                                return (
                                    <div key={idx}>
                                        {raw.blocks.map((blk, bi) => {
                                            switch (blk.type) {
                                                case 'header-one':
                                                    return <h1 key={`h1-${idx}-${bi}`} className="blog-block-h1">{blk.text}</h1>;
                                                case 'header-two':
                                                    return <h2 key={`h2-${idx}-${bi}`} className="blog-block-h2">{blk.text}</h2>;
                                                case 'header-three':
                                                    return <h3 key={`h3-${idx}-${bi}`} className="blog-block-h3">{blk.text}</h3>;
                                                case 'blockquote':
                                                    return <blockquote key={`bq-${idx}-${bi}`} className="blog-block-blockquote">{blk.text}</blockquote>;
                                                default:
                                                    return <p key={`p-${idx}-${bi}`} className="blog-block-paragraph">{blk.text}</p>;
                                            }
                                        })}
                                    </div>
                                );
                            }
                        }
                        return (
                            <p key={idx} className="blog-block-paragraph">
                                {c}
                            </p>
                        );
                    }
                } catch (_) {}
                return (
                    <p key={idx} className="blog-block-paragraph">
                        {typeof c === 'string' ? c : ''}
                    </p>
                );
            }
            return null;
        });
    };

    useEffect(() => {
        if (!countryId) {
            navigate('/');
            return;
        }

        // Fetch country info
        axiosPost(
            'countries/get',
            { 'Content-Type': 'application/json' },
            { id: parseInt(countryId) },
            (resp) => {
                if (resp?.success && resp?.data && resp.data.length > 0) {
                    setCountryName(resp.data[0].name || '');
                }
            }
        );

        // Fetch Travel Guide content
        axiosPost(
            'travel-guides/get',
            { 'Content-Type': 'application/json' },
            { country_id: parseInt(countryId) },
            (data) => {
                if (data?.success && data?.data) {
                    setTravelGuide(data.data);
                } else {
                    setTravelGuide(null);
                }
                setLoading(false);
            },
            (err) => {
                console.error('Failed to load Travel Guide content:', err);
                setTravelGuide(null);
                setLoading(false);
            }
        );
    }, [countryId, navigate]);

    if (loading) return <div className="blog-loading">Loading...</div>;
    if (!travelGuide) return <div className="blog-not-found">Content not available for this destination.</div>;

    const hasBlocks = travelGuide?.blocks && Array.isArray(travelGuide.blocks) && travelGuide.blocks.length > 0;
    const displayTitle = travelGuide.title || `Plan your ${countryName} Trip`;
    const displayExcerpt = travelGuide.excerpt;
    const toPoints = (val) => String(val || '')
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);
    const highlightPoints = toPoints(travelGuide.highlight_items);
    const notePoints = toPoints(travelGuide.notes_items);
    const hasAside = highlightPoints.length > 0 || notePoints.length > 0;

    return (
        <div className="blog-container" style={{ paddingTop: '140px' }}>
            {/* Hero Image */}
            {travelGuide.image && (
                <img
                    src={travelGuide.image}
                    alt={displayTitle}
                    className="blog-hero"
                />
            )}

            {/* Title */}
            <h1 className="blog-title">{displayTitle}</h1>

            {/* Meta */}
            <div className="blog-meta">
                <span className="blog-category">Travel Guide</span>
                <span className="blog-meta-sep">|</span>
                <span>{countryName}</span>
                {travelGuide.updated_at && (
                    <>
                        <span className="blog-meta-sep">|</span>
                        <span>{formatDate(travelGuide.updated_at)}</span>
                    </>
                )}
            </div>

            {/* Excerpt */}
            {displayExcerpt && (
                <div className="blog-excerpt">{displayExcerpt}</div>
            )}

            {/* Body, with the highlight boxes beside it like the site pages */}
            <div className={hasAside ? 'blog-with-aside' : undefined}>
                <div className="blog-with-aside-main">
                    <div className="blog-blocks">
                        {hasBlocks && renderBlocks(travelGuide.blocks)}
                    </div>
                </div>

                {hasAside && (
                    <div className="blog-highlights">
                        {highlightPoints.length > 0 && (
                            <div className="blog-highlight-box">
                                <h2>{travelGuide.highlight_title || 'Highlights'}</h2>
                                <ul>
                                    {highlightPoints.map((point, i) => <li key={i}>{point}</li>)}
                                </ul>
                            </div>
                        )}
                        {notePoints.length > 0 && (
                            <div className="blog-highlight-box is-notes">
                                <h2>{travelGuide.notes_title || 'Good to know'}</h2>
                                <ul>
                                    {notePoints.map((point, i) => <li key={i}>{point}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Back button */}
            <div style={{ padding: '40px 175px' }}>
                <button 
                    onClick={() => navigate(-1)}
                    style={{
                        padding: '12px 28px',
                        background: '#2c2c2c',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontFamily: 'Lato, sans-serif'
                    }}
                >
                    ← Back
                </button>
            </div>

            {/* Lightbox Overlay */}
            {lightboxOpen && lightboxImages.length > 0 && (
                <div className="lightbox-backdrop" onClick={closeLightbox}>
                    <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
                        <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">×</button>
                        {lightboxImages.length > 1 && (
                            <>
                                <button className="lightbox-nav lightbox-prev" onClick={showPrev} aria-label="Previous">‹</button>
                                <button className="lightbox-nav lightbox-next" onClick={showNext} aria-label="Next">›</button>
                            </>
                        )}
                        <img src={lightboxImages[lightboxIndex]} alt="" className="lightbox-image" />
                    </div>
                </div>
            )}
        </div>
    );
}
